import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type RoleSummary = {
  id: string;
  name: string;
  description: string | null;
  isBuiltIn: boolean;
  /**
   * VL-73: derived from a store system's cashier group — read-only while its
   * source exists. Absent from an older Back Office.
   */
  sourceDerived?: boolean;
  userCount: number;
  permissionCodes: string[];
};

export type PermissionRow = {
  code: string;
  description: string | null;
  module: string;
};

const LIST_KEY = ['roles'] as const;
const ONE_KEY = (id: string) => ['roles', id] as const;
const PERMS_KEY = ['roles', 'permissions'] as const;

export function useRoles() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<RoleSummary[]> => {
      const res = await apiClient.get<RoleSummary[]>('/roles');
      return res.data;
    },
  });
}

export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['roles', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<RoleSummary> => {
      const res = await apiClient.get<RoleSummary>(`/roles/${id}`);
      return res.data;
    },
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: PERMS_KEY,
    queryFn: async (): Promise<PermissionRow[]> => {
      const res = await apiClient.get<PermissionRow[]>('/roles/permissions');
      return res.data;
    },
  });
}

export type RoleCreateBody = {
  name: string;
  description?: string | null;
  permissionCodes: string[];
};

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RoleCreateBody) => {
      const res = await apiClient.post<RoleSummary>('/roles', body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export type RoleUpdateBody = Partial<RoleCreateBody>;

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: RoleUpdateBody }) => {
      const res = await apiClient.patch<RoleSummary>(`/roles/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/roles/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}
